import { useState, forwardRef, useImperativeHandle } from 'react'
import { Form, Row, Col } from 'react-bootstrap'
import { BsFloppyFill } from 'react-icons/bs'
import { XCircleFill } from 'react-bootstrap-icons'
import ReactInputMask from 'react-input-mask'
import { toast } from 'react-toastify'
import { useLocation } from 'react-router-dom'

import { CustomButton } from '../../CustomButton'
import { CustomSpinner } from '../../CustomSpinner'
import apinode from '../../../services/apinode'
import { apiCep } from '../../../services/apiCep'
import { formatDate } from '../../../utils/formatDate'
import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import './styles.css'

const TIPOS_CONTRIBUINTE = [
  { label: 'Contribuinte ICMS', value: '1' },
  { label: 'Contribuinte Isento', value: '2' },
  { label: 'Não Contribuinte', value: '9' },
  { label: 'Não Selecionado', value: '99' },
]

const TIPOS_CADASTRO = [
  { name: 'pes_cliente',        label: 'Cliente' },
  { name: 'pes_fornecedor',     label: 'Fornecedor' },
  { name: 'pes_transportadora', label: 'Transportadora' },
  { name: 'pes_fabricante',     label: 'Fabricante' },
  { name: 'pes_funcionario',    label: 'Funcionário' },
  { name: 'pes_parceiro',       label: 'Parceiro' },
  { name: 'pes_prestador',      label: 'Prestador' },
]

const INITIAL_STATE = {
  pes_cliente: 'S', pes_fornecedor: 'N', pes_transportadora: 'N',
  pes_fabricante: 'N', pes_funcionario: 'N', pes_parceiro: 'N', pes_prestador: 'N',
  cod_pessoa: '', pes_data_alteracao: '', tipo_cadastro: 'pes_cliente',
  cod_tipo_contribuinte: '1', pes_tipo: 'J', pes_consumidor_final: 'SIM',
  pes_razao: '', pes_fantasia: '', pes_data_fund_nasc: '', pes_cnpj_cpf: '',
  pes_insc_estadual: '', pes_insc_municipal: '', pes_cep: '', pes_ibge: '',
  pes_logradouro: '', pes_numero: '', pes_complemento: '', pes_bairro: '',
  pes_cidade: '', pes_uf: '', pes_telefone: '', pes_celular: '',
  pes_contato: '', pagamento: '', pes_email: '', pes_email_nfe: '',
}

export const FormClientes = forwardRef(
  ({ onSubmit, isRequestLoading, onBack, title }, ref) => {
    const [customer, setCustomer] = useState(INITIAL_STATE)
    const [isCNPJSearchLoading, setIsCNPJSearchLoading] = useState(false)
    const [isCEPSearchLoading, setIsCEPSearchLoading]   = useState(false)

    const { pathname } = useLocation()
    const isJuridical = customer.pes_tipo === 'J'
    const inEditPage  = pathname !== '/clientes/novo' && !customer.cod_pessoa

    function handleChange(e) {
      const { name, value } = e.target
      setCustomer((prev) => ({ ...prev, [name]: value }))
    }

    function toggleCadastro(name) {
      setCustomer((prev) => ({ ...prev, [name]: prev[name] === 'S' ? 'N' : 'S' }))
    }

    async function getCompanyInfo(e) {
      const value = e.target.value.replace(/\D/g, '')
      if (value.length !== 14) return
      setIsCNPJSearchLoading(true)
      try {
        const { data } = await apinode.get(`/cnpj?cnpj=${value}`)
        setCustomer((prev) => ({
          ...prev,
          pes_razao:      data.razao_social || '',
          pes_fantasia:   data.nome_fantasia || '',
          pes_data_fund_nasc: data.data_inicio || '',
          pes_telefone:   data.telefone1 || '',
          pes_logradouro: data.endereco.logradouro || '',
          pes_numero:     data.endereco.numero || '',
          pes_bairro:     data.endereco.bairro || '',
          pes_cidade:     data.endereco.municipio || '',
          pes_uf:         data.endereco.uf || '',
          pes_cep:        data.endereco.cep || '',
          pes_complemento: data.endereco.complemento || '',
        }))
        try {
          const { data: cep } = await apiCep.get(`/${data.endereco.cep}/json/`)
          setCustomer((prev) => ({ ...prev, pes_ibge: cep.ibge }))
        } catch {
          setCustomer((prev) => ({ ...prev, pes_ibge: '' }))
        }
      } catch (err) {
        sendErrorApiMessage(err, 'Erro ao buscar CNPJ, insira manualmente!')
      } finally {
        setIsCNPJSearchLoading(false)
      }
    }

    async function getCEPInfo(e) {
      const value = e.target.value.replace(/\D/g, '')
      if (value.length !== 8) return
      setIsCEPSearchLoading(true)
      try {
        const { data } = await apiCep.get(`/${value}/json/`)
        setCustomer((prev) => ({
          ...prev,
          pes_logradouro:  data.logradouro || '',
          pes_bairro:      data.bairro || '',
          pes_cidade:      data.localidade || '',
          pes_uf:          data.uf || '',
          pes_complemento: data.complemento || '',
          pes_ibge:        data.ibge,
        }))
      } catch (error) {
        console.error(error)
        toast.error('Erro ao buscar CEP, insira manualmente!')
      } finally {
        setIsCEPSearchLoading(false)
      }
    }

    useImperativeHandle(ref, () => ({
      getCustomer:  () => customer,
      sendCustomer: (c) => setCustomer(c),
    }), [customer])

    if (inEditPage) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CustomSpinner />
        </div>
      )
    }

    return (
      <Form onSubmit={onSubmit} style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* Cabeçalho */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{title}</h1>
            {customer.cod_pessoa && (
              <p style={{ fontSize: 12, color: '#888', margin: 0, marginTop: 2 }}>
                Código #{customer.cod_pessoa}
                {customer.pes_data_alteracao && ` · Alterado em ${formatDate(customer.pes_data_alteracao)}`}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <CustomButton color="#e81414" onClick={onBack} type="button">
              <XCircleFill size={14} /> Cancelar
            </CustomButton>
            <CustomButton
              color="#088c04"
              type="submit"
              isLoading={isRequestLoading}
              isDisabled={isCEPSearchLoading || isCNPJSearchLoading}
            >
              <BsFloppyFill size={14} />
              {isRequestLoading ? 'Salvando...' : 'Salvar'}
            </CustomButton>
          </div>
        </div>

        {/* Seção: Tipo de Cadastro */}
        <Section title="Tipo de cadastro">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TIPOS_CADASTRO.map(({ name, label }) => {
              const ativo = customer[name] === 'S'
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleCadastro(name)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    border: `1px solid ${ativo ? '#185FA5' : '#dee2e6'}`,
                    background: ativo ? '#185FA5' : '#fafafa',
                    color: ativo ? '#fff' : '#555',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </Section>

        {/* Seção: Classificação */}
        <Section title="Classificação">
          <Row className="g-3">
            <Col sm={4}>
              <FieldLabel>Contribuinte</FieldLabel>
              <Form.Select name="cod_tipo_contribuinte" value={customer.cod_tipo_contribuinte} onChange={handleChange} required style={selectStyle}>
                {TIPOS_CONTRIBUINTE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Col>
            <Col sm={4}>
              <FieldLabel>Tipo de pessoa</FieldLabel>
              <Form.Select name="pes_tipo" value={customer.pes_tipo} onChange={handleChange} required style={selectStyle}>
                <option value="F">Física</option>
                <option value="J">Jurídica</option>
              </Form.Select>
            </Col>
            <Col sm={4}>
              <FieldLabel>Consumidor final</FieldLabel>
              <Form.Select name="pes_consumidor_final" value={customer.pes_consumidor_final} onChange={handleChange} required style={selectStyle}>
                <option value="SIM">Sim</option>
                <option value="">Não</option>
              </Form.Select>
            </Col>
          </Row>
        </Section>

        {/* Seção: Dados Principais */}
        <Section title="Dados principais">
          <Row className="g-3">
            <Col sm={5}>
              <FieldLabel required>Razão Social</FieldLabel>
              <Form.Control name="pes_razao" value={customer.pes_razao || ''} onChange={handleChange} required style={inputStyle} />
            </Col>
            <Col sm={4}>
              <FieldLabel>Apelido / Fantasia</FieldLabel>
              <Form.Control name="pes_fantasia" value={customer.pes_fantasia || ''} onChange={handleChange} style={inputStyle} />
            </Col>
            <Col sm={3}>
              <FieldLabel>Data fund. / nasc.</FieldLabel>
              <Form.Control
                type="date"
                name="pes_data_fund_nasc"
                value={formatDate(customer.pes_data_fund_nasc, 'yyyy-MM-dd') || ''}
                onChange={handleChange}
                style={inputStyle}
              />
            </Col>
          </Row>
        </Section>

        {/* Seção: Documentos */}
        <Section title="Documentos">
          <Row className="g-3">
            <Col sm={4}>
              <FieldLabel required>
                {isJuridical ? 'CNPJ' : 'CPF'}
                {isCNPJSearchLoading && <CustomSpinner size={13} style={{ marginLeft: 6 }} />}
              </FieldLabel>
              <ReactInputMask
                required
                mask={isJuridical ? '99.999.999/9999-99' : '999.999.999-99'}
                value={customer.pes_cnpj_cpf || ''}
                onChange={handleChange}
                onBlur={isJuridical ? getCompanyInfo : undefined}
              >
                {(inputProps) => <Form.Control name="pes_cnpj_cpf" {...inputProps} style={inputStyle} />}
              </ReactInputMask>
            </Col>
            <Col sm={4}>
              <FieldLabel>Insc. Estadual</FieldLabel>
              <Form.Control name="pes_insc_estadual" value={customer.pes_insc_estadual || ''} onChange={handleChange} style={inputStyle} />
            </Col>
            <Col sm={4}>
              <FieldLabel>Insc. Municipal</FieldLabel>
              <Form.Control name="pes_insc_municipal" value={customer.pes_insc_municipal || ''} onChange={handleChange} style={inputStyle} />
            </Col>
          </Row>
        </Section>

        {/* Seção: Endereço */}
        <Section title="Endereço">
          <Row className="g-3">
            <Col sm={3}>
              <FieldLabel required>
                CEP
                {isCEPSearchLoading && <CustomSpinner size={13} style={{ marginLeft: 6 }} />}
              </FieldLabel>
              <ReactInputMask
                required
                mask="99999-999"
                value={customer.pes_cep || ''}
                onChange={handleChange}
                onBlur={getCEPInfo}
              >
                {(inputProps) => <Form.Control name="pes_cep" {...inputProps} style={inputStyle} />}
              </ReactInputMask>
            </Col>
            <Col sm={6}>
              <FieldLabel required>Logradouro</FieldLabel>
              <Form.Control name="pes_logradouro" value={customer.pes_logradouro || ''} onChange={handleChange} required style={inputStyle} />
            </Col>
            <Col sm={3}>
              <FieldLabel required>Número</FieldLabel>
              <Form.Control name="pes_numero" value={customer.pes_numero || ''} onChange={handleChange} required style={inputStyle} />
            </Col>
          </Row>
          <Row className="g-3 mt-0">
            <Col sm={3}>
              <FieldLabel>Complemento</FieldLabel>
              <Form.Control name="pes_complemento" value={customer.pes_complemento || ''} onChange={handleChange} style={inputStyle} />
            </Col>
            <Col sm={3}>
              <FieldLabel required>Bairro</FieldLabel>
              <Form.Control name="pes_bairro" value={customer.pes_bairro || ''} onChange={handleChange} required style={inputStyle} />
            </Col>
            <Col sm={4}>
              <FieldLabel required>Cidade</FieldLabel>
              <Form.Control name="pes_cidade" value={customer.pes_cidade || ''} onChange={handleChange} required style={inputStyle} />
            </Col>
            <Col sm={2}>
              <FieldLabel required>UF</FieldLabel>
              <Form.Control name="pes_uf" value={customer.pes_uf || ''} onChange={handleChange} required maxLength={2} style={{ ...inputStyle, textTransform: 'uppercase' }} />
            </Col>
          </Row>
        </Section>

        {/* Seção: Contato */}
        <Section title="Contato">
          <Row className="g-3">
            <Col sm={4}>
              <FieldLabel>Telefone fixo</FieldLabel>
              <ReactInputMask mask="(99) 9999-9999" value={customer.pes_telefone || ''} onChange={handleChange}>
                {(inputProps) => <Form.Control name="pes_telefone" {...inputProps} style={inputStyle} />}
              </ReactInputMask>
            </Col>
            <Col sm={4}>
              <FieldLabel>Celular</FieldLabel>
              <ReactInputMask mask="(99) 99999-9999" value={customer.pes_celular || ''} onChange={handleChange}>
                {(inputProps) => <Form.Control name="pes_celular" {...inputProps} style={inputStyle} />}
              </ReactInputMask>
            </Col>
            <Col sm={4}>
              <FieldLabel>Contato</FieldLabel>
              <ReactInputMask mask="(99) 99999-9999" value={customer.pes_contato || ''} onChange={handleChange}>
                {(inputProps) => <Form.Control name="pes_contato" {...inputProps} style={inputStyle} />}
              </ReactInputMask>
            </Col>
            <Col sm={4}>
              <FieldLabel>E-mail</FieldLabel>
              <Form.Control name="pes_email" type="email" value={customer.pes_email || ''} onChange={handleChange} style={inputStyle} />
            </Col>
            <Col sm={4}>
              <FieldLabel>E-mail NF-e</FieldLabel>
              <Form.Control name="pes_email_nfe" type="email" value={customer.pes_email_nfe || ''} onChange={handleChange} style={inputStyle} />
            </Col>
            <Col sm={4}>
              <FieldLabel>Dados para pagamento</FieldLabel>
              <Form.Control name="pagamento" value={customer.pagamento || ''} onChange={handleChange} style={inputStyle} />
            </Col>
          </Row>
        </Section>

        {/* Rodapé de ações */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e8e8e8' }}>
          <CustomButton color="#e81414" onClick={onBack} type="button">
            <XCircleFill size={14} /> Cancelar
          </CustomButton>
          <CustomButton
            color="#088c04"
            type="submit"
            isLoading={isRequestLoading}
            isDisabled={isCEPSearchLoading || isCNPJSearchLoading}
          >
            <BsFloppyFill size={14} />
            {isRequestLoading ? 'Salvando...' : 'Salvar'}
          </CustomButton>
        </div>
      </Form>
    )
  },
)

// ---- Helpers de UI ----

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
      </div>
      {children}
    </div>
  )
}

function FieldLabel({ children, required }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
      {children}
      {required && <span style={{ color: '#e70f0f', fontSize: 10 }}>*</span>}
    </div>
  )
}

const inputStyle = { fontSize: 13 }
const selectStyle = { fontSize: 13 }